import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart, LineElement, PointElement, ArcElement, BarElement,
  CategoryScale, LinearScale, Tooltip, Legend, Filler,
} from 'chart.js';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { useTheme } from '../theme.jsx';
import { fmtPKR, MONTHS, avatarColor } from '../components/ui.jsx';

Chart.register(LineElement, PointElement, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

// Departments are user-created and open-ended, so colors are hashed per name
// (same scheme as avatars/badges) rather than a fixed enum → color map.

const chartTheme = (dark) => ({
  tooltip: dark
    ? { backgroundColor: '#101a2f', titleColor: '#edf2fa', bodyColor: '#9fadc4',
        borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1, padding: 10, cornerRadius: 10 }
    : { backgroundColor: '#ffffff', titleColor: '#0e1729', bodyColor: '#475469',
        borderColor: 'rgba(15,23,42,0.1)', borderWidth: 1, padding: 10, cornerRadius: 10 },
  grid: { color: dark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.06)' },
  tick: { color: dark ? '#66738c' : '#8a94a8', font: { size: 11 } },
  legend: { color: dark ? '#9fadc4' : '#475469', boxWidth: 12, boxHeight: 12, font: { size: 11 } },
  ring: dark ? '#101a2f' : '#ffffff',
});

export default function Dashboard() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const t = chartTheme(theme === 'dark');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/dashboard').then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="empty-state"><p>{error}</p></div>;
  if (!data) return <div className="empty-state"><p>Loading dashboard…</p></div>;

  const trendLabels = data.attendanceTrend.map((d) =>
    new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));

  const deptData = data.departmentDistribution.filter((d) => d.count > 0);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi-card blue" role="button" tabIndex={0}
          onClick={() => can('employees.view') && navigate('/employees')}
          onKeyDown={(e) => e.key === 'Enter' && can('employees.view') && navigate('/employees')}>
          <div className="kpi-icon">👥</div>
          <div className="kpi-value">{data.totalEmployees}</div>
          <div className="kpi-label">Active Employees</div>
          <div className="kpi-sub">{data.activeSites} active site{data.activeSites === 1 ? '' : 's'}</div>
        </div>
        <div className="kpi-card green" role="button" tabIndex={0}
          onClick={() => can('attendance.view') && navigate('/attendance')}
          onKeyDown={(e) => e.key === 'Enter' && can('attendance.view') && navigate('/attendance')}>
          <div className="kpi-icon">✓</div>
          <div className="kpi-value">{data.attendanceRate}%</div>
          <div className="kpi-label">Attendance Today</div>
          <div className="kpi-sub">{data.todayPresent} present · {data.todayAbsent} absent · {data.todayHalfDay} half-day</div>
        </div>
        <div className="kpi-card amber" role="button" tabIndex={0}
          onClick={() => can('advances.view') && navigate('/advances')}
          onKeyDown={(e) => e.key === 'Enter' && can('advances.view') && navigate('/advances')}>
          <div className="kpi-icon">💰</div>
          <div className="kpi-value">{data.pendingAdvances}</div>
          <div className="kpi-label">Pending Advance Requests</div>
          <div className="kpi-sub">Awaiting HR approval</div>
        </div>
        {data.latestPayroll ? (
          <div className="kpi-card purple" role="button" tabIndex={0}
            onClick={() => navigate(`/payroll/${data.latestPayroll.id}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/payroll/${data.latestPayroll.id}`)}>
            <div className="kpi-icon">📄</div>
            <div className="kpi-value">{fmtPKR(data.latestPayroll.totalNet)}</div>
            <div className="kpi-label">{MONTHS[data.latestPayroll.month - 1]} {data.latestPayroll.year} Payroll ({data.latestPayroll.status.toLowerCase()})</div>
            <div className="kpi-sub">Net payable across workforce</div>
          </div>
        ) : (
          <div className="kpi-card purple" role="button" tabIndex={0}
            onClick={() => can('payroll.view') && navigate('/payroll')}
            onKeyDown={(e) => e.key === 'Enter' && can('payroll.view') && navigate('/payroll')}>
            <div className="kpi-icon">📄</div>
            <div className="kpi-value">—</div>
            <div className="kpi-label">Payroll</div>
            <div className="kpi-sub">{can('payroll.view') ? 'No runs yet' : 'Managed by HR'}</div>
          </div>
        )}
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card-title">Attendance Trend <span>· last 30 days</span></div>
          <div className="chart-wrap">
            <Line
              key={theme}
              data={{
                labels: trendLabels,
                datasets: [
                  {
                    label: 'Present', data: data.attendanceTrend.map((d) => d.present),
                    borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.12)',
                    fill: true, tension: 0.35, borderWidth: 2, pointRadius: 0, pointHitRadius: 12,
                  },
                  {
                    label: 'Absent', data: data.attendanceTrend.map((d) => d.absent),
                    borderColor: '#ef4444', backgroundColor: 'transparent',
                    tension: 0.35, borderWidth: 2, pointRadius: 0, pointHitRadius: 12,
                  },
                ],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  legend: { labels: t.legend },
                  tooltip: t.tooltip,
                },
                scales: { x: { grid: t.grid, ticks: { ...t.tick, maxTicksLimit: 10 } },
                  y: { grid: t.grid, ticks: t.tick, beginAtZero: true } },
              }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Workforce by Department</div>
          <div className="chart-wrap">
            <Doughnut
              key={theme}
              data={{
                labels: deptData.map((d) => d.department),
                datasets: [{
                  data: deptData.map((d) => d.count),
                  backgroundColor: deptData.map((d) => avatarColor(d.department)),
                  borderColor: t.ring, borderWidth: 2,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false, cutout: '68%',
                plugins: {
                  legend: { position: 'bottom', labels: t.legend },
                  tooltip: t.tooltip,
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Headcount by Site</div>
        <div className="chart-wrap">
          <Bar
            key={theme}
            data={{
              labels: data.siteDistribution.map((s) => s.site),
              datasets: [{
                label: 'Employees', data: data.siteDistribution.map((s) => s.count),
                backgroundColor: '#3b82f6', borderRadius: 6, maxBarThickness: 48,
              }],
            }}
            options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: t.tooltip },
              scales: { x: { grid: { display: false }, ticks: t.tick },
                y: { grid: t.grid, ticks: t.tick, beginAtZero: true } },
            }}
          />
        </div>
      </div>
    </>
  );
}
